/**
 * Accent-family context (KAN-648).
 *
 * The sibling of ``ThemeProvider``, deliberately the same shape: it owns
 * the ``choice`` state, keeps a localStorage mirror, adopts the account
 * value (``AuthMe.ui_accent``) once so the preference follows the user
 * across devices, and puts ``data-accent`` on <html>.
 *
 * 🪤 **Theme and accent do not need to know about each other.** The
 * handoff prototype recomputed the accent on every theme switch because it
 * pushed the four variables inline; here each family ships a light block
 * and a dark block and the cascade picks — flipping ``.dark`` re-resolves
 * the accent with no coordination between the two providers. That is the
 * whole reason ``applyAccent`` writes an attribute rather than
 * ``style.setProperty``; see ``lib/accent.ts``.
 *
 * Scope: <html>, like the theme, so portals (drawers, modals, the command
 * palette) inherit it. Inside a **tenant** shell the tenant's brand colour
 * still wins — ``TenantBrandTheme`` sets the same four variables as an
 * inline style on a wrapping div, and an inline style outranks any
 * selector on <html> for that subtree. That is the intended priority: a
 * shop wears its own colour, and the personal accent is what a platform
 * admin (and the Ingot doc web) gets to choose.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { useAuthMe, useUpdateProfile } from "@/api/queries";
import {
  applyAccent,
  isAccentChoice,
  readStoredAccent,
  writeStoredAccent,
  type AccentChoice,
} from "@/lib/accent";

interface AccentContextValue {
  choice: AccentChoice;
  setChoice: (choice: AccentChoice) => void;
}

const AccentContext = createContext<AccentContextValue>({
  choice: "blue",
  setChoice: () => {},
});

export function AccentProvider({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  const [choice, setChoiceState] = useState<AccentChoice>(() =>
    readStoredAccent(),
  );

  const me = useAuthMe();
  const serverAccent = me.data?.ui_accent;
  const updateProfile = useUpdateProfile();

  // Adopt the account value ONCE, the first time it's known — this is how a
  // preference set on another device lands here. After that the local
  // switcher wins and pushes to the server, so a stale ``/auth/me`` refetch
  // can't revert a fresh choice. (Same reasoning as ThemeProvider.)
  const initedFromServer = useRef(false);
  useEffect(() => {
    if (initedFromServer.current || serverAccent === undefined) return;
    initedFromServer.current = true;
    // A row written by an older client — or by a future family this build
    // does not know — must not wedge the UI: fall through to the mirror.
    if (!isAccentChoice(serverAccent)) return;
    if (serverAccent !== readStoredAccent()) {
      setChoiceState(serverAccent);
      writeStoredAccent(serverAccent);
    }
  }, [serverAccent]);

  const setChoice = useCallback(
    (next: AccentChoice) => {
      setChoiceState(next);
      writeStoredAccent(next);
      // Signed-in → persist on the account so it follows the user across
      // devices. Anonymous → localStorage-only (nothing to attach to).
      if (me.data) updateProfile.mutate({ ui_accent: next });
    },
    [me.data, updateProfile],
  );

  useLayoutEffect(() => {
    applyAccent(choice);
  }, [choice]);

  return (
    <AccentContext.Provider value={{ choice, setChoice }}>
      {children}
    </AccentContext.Provider>
  );
}

export function useAccent(): AccentContextValue {
  return useContext(AccentContext);
}
